import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Mail, Phone, MapPin, Sparkles, FileText } from "lucide-react";
import ChatPanel from "@/components/chat/ChatPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = [
  "Demand Generation",
  "ABM Programme",
  "Content Syndication",
  "Paid Media",
  "Webinars & Events",
  "General enquiry",
];

export default function Contact() {
  const [tab, setTab] = useState("form");
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    role: "",
    interest: "Demand Generation",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill name, email, and a short message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thanks \u2014 we\u2019ll route your enquiry to a strategist within 24 hours.");
      setForm({ name: "", email: "", company: "", role: "", interest: "Demand Generation", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please email sales@revgrow.co.uk directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 md:py-32 border-t border-white/5">
      <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-teal mb-4">
              Let{"\u2019"}s talk
            </div>
            <h2 className="font-display font-semibold text-4xl md:text-5xl lg:text-[64px] leading-[0.98] tracking-[-0.02em]">
              Let{"\u2019"}s build <span className="italic font-normal text-teal">your pipeline.</span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-md leading-relaxed">
              AI-Powered Orchestration for High-Growth Teams. Book a call with a
              RevGrow strategist and get a demand-gen plan tuned to your ICP and
              pipeline goals.
            
            </p>

            <div className="mt-10 space-y-5">
              <div className="flex items-start gap-3">
                <Mail className="text-teal mt-0.5" size={16} />
                <div>
                  <div className="text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground mb-1">Email</div>
                  <a href="mailto:sales@revgrow.co.uk" className="text-sm hover:text-teal">
                    sales@revgrow.co.uk
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Phone className="text-teal mt-0.5" size={16} />
                <div>
                  <div className="text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground mb-1">United Kingdom</div>
                  <a href="tel:+441174635393" className="text-sm hover:text-teal">
                    +44 117 463 5393
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="text-teal mt-0.5" size={16} />
                <div>
                  <div className="text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground mb-1">Registered Office</div>
                  <div className="text-sm">87 Lozells Street, Birmingham B19 2AP</div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            {/* Tab switcher */}
            <div className="inline-flex items-center rounded-full border border-white/10 p-1 mb-4">
              <button
                data-testid="contact-tab-form"
                onClick={() => setTab("form")}
                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-medium transition ${
                  tab === "form" ? "bg-teal text-[#050A0F]" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <FileText size={13} /> Contact form
              </button>
              <button
                data-testid="contact-tab-qualifier"
                onClick={() => setTab("qualifier")}
                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-medium transition ${
                  tab === "qualifier" ? "bg-teal text-[#050A0F]" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Sparkles size={13} /> Chat with our AI strategist first
              </button>
            </div>

            {tab === "qualifier" ? (
              <ChatPanel
                mode="qualifier"
                sessionKey="qualifier"
                variant="inline"
                title="AI-guided discovery"
                subtitle="~5 questions · Gemini 2.5 Flash"
                placeholder="Type your answer..."
                initialMessage={"Hi — I'm the RevGrow AI strategist. Before you book a call, I'd love to understand your world. What growth challenge brought you to RevGrow today?"}
                suggestions={[
                  "We need more pipeline in EMEA",
                  "Our lead quality is low",
                  "We're launching a new product",
                ]}
                onLeadReady={() => setTab("form")}
              />
            ) : (
              <form
                data-testid="contact-form"
                onSubmit={submit}
                className="rounded-2xl border border-white/[0.06] bg-[#0A121A] p-6 md:p-10"
              >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Name" required>
                  <input
                    data-testid="contact-name"
                    value={form.name}
                    onChange={setField("name")}
                    className="input"
                    placeholder="Ada Lovelace"
                  />
                </Field>
                <Field label="Work email" required>
                  <input
                    data-testid="contact-email"
                    type="email"
                    value={form.email}
                    onChange={setField("email")}
                    className="input"
                    placeholder="ada@company.com"
                  />
                </Field>
                <Field label="Company">
                  <input
                    data-testid="contact-company"
                    value={form.company}
                    onChange={setField("company")}
                    className="input"
                    placeholder="Company Ltd."
                  />
                </Field>
                <Field label="Role">
                  <input
                    data-testid="contact-role"
                    value={form.role}
                    onChange={setField("role")}
                    className="input"
                    placeholder="Head of Growth"
                  />
                </Field>
                <div className="md:col-span-2">
                  <Field label="Interest">
                    <select
                      data-testid="contact-interest"
                      value={form.interest}
                      onChange={setField("interest")}
                      className="input"
                    >
                      {INTERESTS.map((i) => <option key={i} value={i} className="bg-[#0A121A]">{i}</option>)}
                    </select>
                  </Field>
                </div>
                <div className="md:col-span-2">
                  <Field label="Message" required>
                    <textarea
                      data-testid="contact-message"
                      value={form.message}
                      onChange={setField("message")}
                      className="input h-32 resize-none"
                      placeholder="Tell us about your ICP and pipeline goals..."
                    />
                  </Field>
                </div>
              </div>
              <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
                <div className="text-xs text-muted-foreground max-w-md">
                  By submitting, you agree to our privacy policy. We{"\u2019"}ll route your
                  enquiry to a strategist within 24 hours.
                </div>
                <button
                  data-testid="contact-submit"
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center gap-1.5 rounded-full bg-teal text-[#050A0F] px-6 py-3 text-[14px] font-medium hover:opacity-90 transition disabled:opacity-50"
                >
                  {submitting ? "Sending..." : "Book a strategy call"} <ArrowUpRight size={16} />
                </button>
              </div>
            </form>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .input {
          width: 100%;
          background: rgba(0,0,0,0.35);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 10px;
          padding: 12px 14px;
          font-size: 14px;
          color: white;
          outline: none;
          transition: border-color 0.2s;
        }
        .input:focus { border-color: rgba(0,229,194,0.5); }
        .input::placeholder { color: rgba(255,255,255,0.28); }
      `}</style>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-muted-foreground mb-2">
        {label}{required && <span className="text-teal"> *</span>}
      </div>
      {children}
    </label>
  );
}
